import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Form, ProgressBar, Spinner } from 'react-bootstrap';

import { ClientSelectGroup } from '@/components/ClientSelectGroup';
import { ToolPage } from '@/components/ui/ToolPage';
import { useAuth } from '@/contexts/AuthContext';
import { clientService } from '@/services/clientService';
import {
  passwordResetService,
  ResetRefused,
  type ResetPreview,
  type ResetRun,
  type ResetSkipReason,
} from '@/services/passwordResetService';
import type { Client } from '@/types';

/** How often the page asks how a run is getting on. */
const POLL_MS = 3000;

const REASON_TEXT: Record<ResetSkipReason, string> = {
  you: 'the person who started it',
  'system-user': 'the Numa system account',
  'single-sign-on': 'sign in with single sign-on, so have no Numa password',
  disabled: 'are turned off',
  'already-reset': 'must already set a new password',
  'not-activated': 'were invited and have not set a password yet',
  'no-verified-email': 'have no verified email, so the reset email cannot reach them',
};

const matches = (typed: string, name: string) => name !== '' && typed.trim().toLowerCase() === name.toLowerCase();

/**
 * Tools > Reset everyone's password (FEAT-690). For one client at a time,
 * usually after a breach: every person must set a new password, and every
 * session ends at once.
 *
 * NOTHING HAPPENS ON ONE CLICK. Choosing a client only reads. The page lists
 * who the reset reaches and who it leaves out, and the button stays off until
 * the client name is typed. The client's own Lambda checks the name and the
 * list again, and refuses a list that changed since this page read it.
 */
export default function ResetAllPasswordsTool() {
  const { user } = useAuth();
  const by = user?.email || user?.username || '';

  const [clients, setClients] = useState<Client[]>([]);
  const [clientName, setClientName] = useState('');
  const client = clients.find((c) => c.name === clientName);

  const [preview, setPreview] = useState<ResetPreview | null>(null);
  const [run, setRun] = useState<ResetRun | null>(null);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setClients(await clientService.getAllClients());
      } catch (e) {
        const detail = e instanceof Error ? ` ${e.message}` : '';
        setError(`Could not load the client list. Reload the page to try again.${detail}`);
      }
    })();
  }, []);

  // A new client starts from nothing: no list, no name typed.
  useEffect(() => {
    setPreview(null);
    setRun(null);
    setTyped('');
    setNotice(null);
    setError(null);
  }, [clientName]);

  const load = useCallback(async () => {
    if (!client) return;
    setLoading(true);
    setError(null);
    setNotice(null);
    try {
      const next = await passwordResetService.preview(client);
      setPreview(next);
      setRun(next.run);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [client]);

  const running = run?.status === 'running';

  // While a run is going, ask how it is getting on.
  useEffect(() => {
    if (!client || !running) return;
    const timer = window.setInterval(async () => {
      try {
        const next = await passwordResetService.status(client);
        setPreview(next);
        setRun(next.run);
      } catch {
        // A missed poll is not a failed run: the next one tries again.
      }
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [client, running]);

  const onStart = async () => {
    if (!client || !preview) return;
    setStarting(true);
    setError(null);
    setNotice(null);
    try {
      setRun(await passwordResetService.start(client, by, preview.previewToken, typed));
      setTyped('');
    } catch (e) {
      if (e instanceof ResetRefused) {
        if (e.preview) setPreview(e.preview);
        if (e.run) setRun(e.run);
        if (e.reason.startsWith('preview-')) setTyped('');
        setNotice(e.message);
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setStarting(false);
    }
  };

  const count = preview?.reset.length ?? 0;
  const canStart = !!preview && !running && count > 0 && !!by && matches(typed, preview.workspace);

  return (
    <ToolPage
      title="Reset everyone's password"
      subtitle="Every person in one client's workspace must set a new password, and is signed out everywhere. For a breach."
      width="narrow"
    >
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <Card.Body>
          <div className="tool-form">
            <section className="tool-form__section">
              <h2 className="section-label">Client</h2>
              <ClientSelectGroup value={clientName} onChange={setClientName} clients={clients} />
              <div className="tool-form__actions">
                <Button variant="outline-primary" onClick={() => void load()} disabled={!client || loading}>
                  {loading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Reading the list
                    </>
                  ) : (
                    'Show who it resets'
                  )}
                </Button>
                {!client && <span className="tool-form__hint">Choose a client first.</span>}
              </div>
            </section>

            {preview && run && run.status !== 'finished' && run.status !== 'stopped' && (
              <section className="tool-form__section">
                <h2 className="section-label">Running</h2>
                <p>A reset started by {run.by} is running.</p>
                <ProgressBar now={(run.done / Math.max(run.total, 1)) * 100} animated className="mb-2" />
                <p className="mb-0">
                  {run.done} of {run.total} done. You can leave this page: the reset continues in the workspace.
                </p>
              </section>
            )}

            {preview && run && (run.status === 'finished' || run.status === 'stopped') && (
              <Alert variant={run.failedCount > 0 || run.status === 'stopped' ? 'warning' : 'success'}>
                {run.status === 'stopped'
                  ? `The last reset stopped after ${run.done} of ${run.total}. Start again to reset the rest.`
                  : `The last reset, by ${run.by}: ${run.done - run.failedCount} of ${run.total} passwords were reset.`}
                {run.failures.length > 0 && (
                  <ul className="mb-0 mt-2">
                    {run.failures.map((f) => (
                      <li key={f.email}>
                        {f.email}: {f.error}
                      </li>
                    ))}
                  </ul>
                )}
              </Alert>
            )}

            {preview && !running && (
              <>
                <Alert variant="danger">
                  <strong>You cannot undo this.</strong> Everyone listed below is signed out of{' '}
                  <strong>{preview.workspace}</strong> on every device at once. They cannot use Numa until they set a
                  new password. Each person gets an email with a link to set one.
                </Alert>

                <section className="tool-form__section">
                  <h2 className="section-label">
                    {count === 1 ? '1 person will be reset' : `${count} people will be reset`}
                  </h2>
                  {count > 0 ? (
                    <ul style={{ maxHeight: '14rem', overflowY: 'auto' }} className="mb-0">
                      {preview.reset.map((email) => (
                        <li key={email}>{email}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mb-0">Nobody in this workspace can be reset.</p>
                  )}
                </section>

                {preview.skipped.length > 0 && (
                  <section className="tool-form__section">
                    <h2 className="section-label">Left out</h2>
                    <ul className="mb-0">
                      {(Object.keys(REASON_TEXT) as ResetSkipReason[])
                        .map((reason) => [reason, preview.skipped.filter((s) => s.reason === reason).length] as const)
                        .filter(([, n]) => n > 0)
                        .map(([reason, n]) => (
                          <li key={reason}>
                            {reason === 'system-user' ? REASON_TEXT[reason] : `${n} ${REASON_TEXT[reason]}`}
                          </li>
                        ))}
                    </ul>
                  </section>
                )}

                {notice && <Alert variant="warning">{notice}</Alert>}

                {count > 0 && (
                  <>
                    <Form.Group className="mb-3" controlId="reset-all-confirm">
                      <Form.Label>
                        To confirm, type <strong>{preview.workspace}</strong>
                      </Form.Label>
                      <Form.Control
                        value={typed}
                        onChange={(e) => setTyped(e.target.value)}
                        autoComplete="off"
                        spellCheck={false}
                        placeholder={preview.workspace}
                      />
                    </Form.Group>
                    <div className="tool-form__actions">
                      <Button variant="danger" onClick={() => void onStart()} disabled={!canStart || starting}>
                        {starting ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Starting
                          </>
                        ) : count === 1 ? (
                          'Reset 1 password'
                        ) : (
                          `Reset ${count} passwords`
                        )}
                      </Button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </Card.Body>
      </Card>
    </ToolPage>
  );
}
